import { useNavigate } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

// Material Dashboard 2 React themes
import theme from "assets/theme";
import themeDark from "assets/theme-dark";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Material Dashboard 2 React routes
import getRoutes from "routes";

// Material Dashboard 2 React contexts
import { useMaterialUIController, setMiniSidenav, setOpenConfigurator } from "context";

// Images
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";
import React from "react";
import { useMsal } from "@azure/msal-react"; // For MSAL integration
import ProtectedPage from "layouts/SharpWatchLayouts/ProtectedPage";

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;

  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();

  const { instance } = useMsal();
  const account = instance.getActiveAccount();
  const token = account?.idTokenClaims;
  const userRoles = token?.roles || [];

  const navigate = useNavigate(); // Initialize navigate with useNavigate

  // Get stored role from localStorage or set default
  const [selectedRole, setSelectedRole] = useState(localStorage.getItem("selectedRole") || "");
  const [roleSelectionOpen, setRoleSelectionOpen] = useState(false);
  const [noRolesPopupOpen, setNoRolesPopupOpen] = useState(false); // Popup for no roles assigned

  // Check authentication **before** checking roles
  useEffect(() => {
    const checkAccount = async () => {
      const accounts = instance.getAllAccounts();
      if (accounts.length > 0) {
        instance.setActiveAccount(accounts[0]); // Set first account as active
      }
    };
    checkAccount();
  }, [instance]);

  // Ensure user is **signed in** before checking roles
  useEffect(() => {
    if (!account) return; // Do nothing if user is not signed in

    if (userRoles.length === 0) {
      // If user **has no roles**, show popup
      setNoRolesPopupOpen(true);
    } else if (userRoles.length > 1 && !selectedRole) {
      // If multiple roles, ask the user to select
      setRoleSelectionOpen(true);
    }
  }, [account, userRoles, selectedRole]);

  // Ensure selected role is stored
  const handleRoleSelect = (role) => {
    localStorage.setItem("selectedRole", role);
    setSelectedRole(role);
    setRoleSelectionOpen(false);
    navigate("/training"); // Redirect after role selection
  };

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });
    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enters mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leaves mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Function to toggle the configurator
  const handleConfiguratorOpen = () => {
    setOpenConfigurator(dispatch, !openConfigurator);
  };

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  // Filter routes based on the selected role
  const filteredRoutes = getRoutes().filter((route) => {
    if (!route.allowedRoles) return true; // Allow public routes
    return selectedRole && route.allowedRoles.includes(selectedRole); // Only show selected role's routes
  });

  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  );

  return (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {layout === "dashboard" && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
            brandName="Tesla Subsea"
            routes={filteredRoutes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
        </>
      )}
      <Routes>
        {filteredRoutes.map((route) => (
          <Route
            exact
            path={route.route}
            element={
              route.allowedRoles ? (
                <ProtectedPage allowedRoles={route.allowedRoles}>{route.component}</ProtectedPage>
              ) : (
                route.component
              )
            }
            key={route.key}
          />
        ))}
        <Route path="*" element={<Navigate to="/authentication/sign-in" />} />
      </Routes>

      {/* Role Selection Modal */}
      <Dialog open={roleSelectionOpen} onClose={() => {}}>
        <DialogTitle>Select Your Role</DialogTitle>
        <DialogContent>
          <List>
            {userRoles.map((role, index) => (
              <ListItem key={index}>
                <ListItemButton onClick={() => handleRoleSelect(role)}>
                  <ListItemText primary={role} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => navigate("/authentication/sign-out")} color="secondary">
            Sign Out
          </Button>
        </DialogActions>
      </Dialog>

      {/* No Roles Assigned Popup */}
      <Dialog open={noRolesPopupOpen} onClose={() => setNoRolesPopupOpen(false)}>
        <DialogTitle>No Projects Assigned</DialogTitle>
        <DialogActions>
          <Button onClick={() => navigate("/authentication/sign-out")} color="secondary">
            Sign Out
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
}
